import { ApiBase, HttpMethod } from "../../core/api-base";
import { z } from "zod";
import {
    GetFavorites200Response,
    GetFollowers200Response,
    GetFollowings200Response,
    GetUserPosts200Response,
} from "../../api/generated/users.zod";

interface UserModalApiOptions {
    page?: number;
    perPage?: number;
}

type UserPostsResponse = z.infer<typeof GetUserPosts200Response>;
type FollowersResponse = z.infer<typeof GetFollowers200Response>;
type FollowingsResponse = z.infer<typeof GetFollowings200Response>;
type FavoritesResponse = z.infer<typeof GetFavorites200Response>;

export type UserModalPost = NonNullable<UserPostsResponse["data"]>[number];
export type UserModalFollower = NonNullable<FollowersResponse["data"]>[number];
export type UserModalFollowing = NonNullable<FollowingsResponse["data"]>[number];
export type UserModalFavorite = NonNullable<FavoritesResponse["data"]>[number];

function extractTypedArray<T>(payload: { data?: T[] | null | undefined } | null | undefined): T[] {
    return Array.isArray(payload?.data) ? payload.data : [];
}

export class UserModalApi extends ApiBase {
    public static async getPosts(userId: number, options: UserModalApiOptions = {}): Promise<UserModalPost[]> {
        const page = Number(options.page ?? 1);
        const perPage = Number(options.perPage ?? 100);

        const url = `/api/v1/users/posts/${userId}?per_page=${perPage}&page=${page}`;
        const response = await super.apiRequestWithErrorHandling(url, HttpMethod.Get, null, {
            showErrors: false,
            fallbackMessage: "Failed to load posts",
        });

        const payload = (await response.json()) as UserPostsResponse;
        return extractTypedArray<UserModalPost>(payload);
    }

    public static async getFollowers(userId: number, options: UserModalApiOptions = {}): Promise<UserModalFollower[]> {
        const page = Number(options.page ?? 1);
        const perPage = Number(options.perPage ?? 100);

        const url = `/api/v1/users/followers/${userId}?per_page=${perPage}&page=${page}`;
        const response = await super.apiRequestWithErrorHandling(url, HttpMethod.Get, null, {
            showErrors: false,
            fallbackMessage: "Failed to load followers",
        });

        const payload = (await response.json()) as FollowersResponse;
        return extractTypedArray<UserModalFollower>(payload);
    }

    public static async getFollowings(
        userId: number,
        options: UserModalApiOptions = {},
    ): Promise<UserModalFollowing[]> {
        const page = Number(options.page ?? 1);
        const perPage = Number(options.perPage ?? 100);

        const url = `/api/v1/users/followings/${userId}?per_page=${perPage}&page=${page}`;
        const response = await super.apiRequestWithErrorHandling(url, HttpMethod.Get, null, {
            showErrors: false,
            fallbackMessage: "Failed to load followings",
        });

        const payload = (await response.json()) as FollowingsResponse;
        return extractTypedArray<UserModalFollowing>(payload);
    }

    public static async getFavorites(userId: number, options: UserModalApiOptions = {}): Promise<UserModalFavorite[]> {
        const page = Number(options.page ?? 1);
        const perPage = Number(options.perPage ?? 100);

        const url = `/api/v1/users/favorites/${userId}?per_page=${perPage}&page=${page}`;
        const response = await super.apiRequestWithErrorHandling(url, HttpMethod.Get, null, {
            showErrors: false,
            fallbackMessage: "Failed to load favorites",
        });

        const payload = (await response.json()) as FavoritesResponse;
        return extractTypedArray<UserModalFavorite>(payload);
    }

    public static async removeFavorite(productId: number): Promise<Response> {
        return await super.apiDelete(`/api/v1/products/${productId}/likes`, { noSpinner: true });
    }

    public static async removeFollowing(targetUserId: number): Promise<Response> {
        return await super.apiDelete(`/api/v1/users/follow/${targetUserId}`, { noSpinner: true });
    }
}
